import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Card, Form, Button, Row, Col } from 'react-bootstrap';
import { FaIdCard, FaSave, FaTimes } from 'react-icons/fa';
import Swal from 'sweetalert2';
import Navigation from './Navigation';
import AdminPageHeader from './AdminPageHeader';
import './MembershipForm.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function MembershipForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [validated, setValidated] = useState(false);
  const [formData, setFormData] = useState({
    outlet: '',
    name: '',
    price: '',
    expired: '',
    discount: '0',
    type: ''
  });

  const membershipTypes = [
    { value: 'Daily', label: 'Daily' },
    { value: 'Monthly', label: 'Monthly' }
  ];

  // Fetch available outlets
  const fetchOutlets = useCallback(async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
        Swal.fire('Error', 'Session expired. Please log in again.', 'error');
        return;
    }

    try {
        const response = await fetch(`${API_BASE_URL}outletlist`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ limit: 100, offset: 0 })
        });
        const data = await response.json();
        console.log('Fetched outlets data:', data);  // Improved logging

        if (data.error === "0" && data.results) {
            // Map API response to the expected format
            const formattedOutlets = data.results.map(outlet => ({
                ID: outlet.id.toString(),
                OutletName: outlet.outlet_name
            }));
            setOutlets(formattedOutlets);
        } else {
            console.error('Failed to fetch outlets:', data.message || 'Unknown error');
            Swal.fire('Error', data.message || 'Failed to fetch outlets', 'error');
        }
    } catch (err) {
        console.error('Error fetching outlets:', err);
        Swal.fire('Error', 'Failed to fetch outlets', 'error');
    }
}, []);


  // Fetch membership data for editing
  const fetchMembershipData = useCallback(async () => {
    if (!id) return;
    try {
      const token = localStorage.getItem('sessionToken');
      const response = await fetch(`${API_BASE_URL}membership/get`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ id: parseInt(id, 10) })
      });
      const data = await response.json();
      if (data.error === 0) {
        setFormData(data.data);
      } else {
        throw new Error('Failed to fetch membership data');
      }
    } catch (err) {
      console.error('Error fetching membership data:', err);
      Swal.fire('Error', 'Failed to fetch membership data', 'error');
      navigate('/membership-setting');
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchOutlets();
    if (id) {
      fetchMembershipData();
    }
  }, [id, fetchOutlets, fetchMembershipData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.outlet) return 'Please select an outlet';
    if (!formData.name.trim()) return 'Please enter a membership name';
    if (!formData.price || parseFloat(formData.price) <= 0) return 'Please enter a valid price';
    if (!formData.expired || parseInt(formData.expired, 10) <= 0) return 'Please enter a valid duration';
    if (parseFloat(formData.discount) < 0 || parseFloat(formData.discount) > 100) return 'Discount must be between 0 and 100';
    if (!formData.type) return 'Please select a membership type';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidated(true);

    const error = validateForm();
    if (error) {
      Swal.fire('Validation Error', error, 'error');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('sessionToken');
      const payload = {
        outlet: parseInt(formData.outlet, 10),
        name: formData.name.trim(),
        price: parseFloat(formData.price),
        expired: parseInt(formData.expired, 10),
        discount: parseFloat(formData.discount),
        type: formData.type.trim()
      };

      const url = id ? `${API_BASE_URL}membership/update` : `${API_BASE_URL}membership/add`;
      const method = id ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(id ? { ...payload, id: parseInt(id, 10) } : payload)
      });
      const data = await response.json();

      if (data.error === 0) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: `Membership ${id ? 'updated' : 'created'} successfully`,
          timer: 1500
        }).then(() => navigate('/membership-setting'));
      } else {
        throw new Error(data.message || `Failed to ${id ? 'update' : 'create'} membership`);
      }
    } catch (err) {
      console.error('Error:', err);
      Swal.fire('Error', err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navigation />
      <Container fluid className="admin-page-shell mt-4 px-4 membership-container">
        <AdminPageHeader
          icon={FaIdCard}
          eyebrow="Plan configuration"
          title={id ? 'Edit Plan' : 'Add Plan'}
          description="Kelola paket atau plan dengan struktur harga, durasi, dan outlet yang konsisten dengan standar halaman dashboard."
          breadcrumbs={[
            { label: 'Dashboard', to: '/dashboardadmin' },
            { label: 'Plans', to: '/membership-setting' },
            { label: id ? 'Edit Plan' : 'Add Plan' },
          ]}
        />

        <Card className="membership-card shadow-lg">
          <Card.Body>
            <Form noValidate validated={validated} onSubmit={handleSubmit}>
              {/* Row 1: Outlet and Membership Name */}
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label>Outlet *</Form.Label>
                    <Form.Select
                      name="outlet"
                      value={formData.outlet}
                      onChange={handleChange}
                      required
                      className="form-control custom-radius"
                    >
                      <option value="">Select Outlet</option>
                      {outlets.map(outlet => (
                        <option key={outlet.ID} value={outlet.ID}>
                          {outlet.OutletName}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      Please select an outlet.
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label>Membership Name *</Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      placeholder="Enter membership name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="form-control custom-radius"
                    />
                    <Form.Control.Feedback type="invalid">
                      Please enter a membership name.
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              {/* Row 2: Price and Discount */}
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label>Price (Rp) *</Form.Label>
                    <Form.Control
                      type="number"
                      name="price"
                      placeholder="Enter price"
                      value={formData.price}
                      onChange={handleChange}
                      min="0"
                      required
                      className="form-control custom-radius"
                    />
                    <Form.Control.Feedback type="invalid">
                      Please enter a valid price.
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label>Discount (%)</Form.Label>
                    <Form.Control
                      type="number"
                      name="discount"
                      placeholder="Enter discount"
                      value={formData.discount}
                      onChange={handleChange}
                      min="0"
                      max="100"
                      className="form-control custom-radius"
                    />
                    <Form.Control.Feedback type="invalid">
                      Discount must be between 0 and 100.
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              {/* Row 3: Membership Type and Duration */}
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label>Membership Type *</Form.Label>
                    <Form.Select
                      name="type"
                      value={formData.type}
                      onChange={handleChange}
                      required
                      className="form-control custom-radius"
                    >
                      <option value="">Select Type</option>
                      {membershipTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      Please select a membership type.
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label>Duration *</Form.Label>
                    <Form.Control
                      type="number"
                      name="expired"
                      placeholder={`Enter duration in ${formData.type === 'Daily' ? 'days' : 'months'}`}
                      value={formData.expired}
                      onChange={handleChange}
                      min="1"
                      max={formData.type === 'Daily' ? "31" : "12"}
                      required
                      className="form-control custom-radius"
                    />
                    <Form.Text className="text-muted">
                      {formData.type === 'Daily'
                        ? 'Enter number of days (1-31)'
                        : 'Enter number of months (1-12)'}
                    </Form.Text>
                    <Form.Control.Feedback type="invalid">
                      Please enter a valid duration.
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              {/* Action Buttons */}
              <div className="d-flex justify-content-end gap-3">
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate('/membership-setting')}
                  disabled={loading}
                  className="custom-radius"
                >
                  <FaTimes className="me-1" /> Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={loading}
                  className="custom-radius"
                >
                  <FaSave className="me-1" /> {loading ? 'Saving...' : 'Save Membership'}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
}

export default MembershipForm;
